import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Send, Loader2, Check } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success("Account created! Let's get you set up.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex w-1/2 bg-brand text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-white/15 backdrop-blur grid place-items-center">
            <Send className="w-4 h-4" />
          </div>
          <span className="font-heading text-xl font-semibold">FlowSend</span>
        </div>
        <div className="max-w-md">
          <h2 className="font-heading text-3xl font-semibold tracking-tight">
            Automate WhatsApp like your top team member.
          </h2>
          <p className="text-emerald-100 mt-3">
            The lightweight WhatsApp automation platform for solo founders & small teams.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Send broadcasts to thousands of contacts",
              "Build no-code WhatsApp flows",
              "Track every conversation in one inbox",
              "Get insights on delivery, replies & conversions",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-emerald-100">
          Trusted by 1,000+ small businesses across India
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Card className="border border-slate-200 shadow-none">
            <CardContent className="p-7">
              <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
                Create your account
              </h1>
              <p className="text-sm text-slate-500 mt-1 mb-6">
                Start sending WhatsApp campaigns in minutes
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                    Your name
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="signup-name-input"
                    placeholder="Aarav Sharma"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                    Work email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="signup-email-input"
                    placeholder="you@company.com"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="signup-password-input"
                    minLength={6}
                    required
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">At least 6 characters</p>
                </div>
                <Button
                  type="submit"
                  data-testid="signup-submit-button"
                  disabled={loading}
                  className="w-full bg-brand hover:bg-brand-dark text-white"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-brand-dark font-medium hover:underline" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
